import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGem } from '@/icons';
import { Modal, ELEVATED_MODAL_Z_INDEX } from './Modal';
import { useTheme } from '../contexts/ThemeContext';

interface ProTrialEndedModalProps {
    isOpen: boolean;
    /** Prominent path: opens the normal self-upgrade flow. */
    onUpgradeClick: () => void;
    /** Quieter path: opens the downgrade confirmation. */
    onDowngradeClick: () => void;
}

/**
 * ProTrial: shown to any member of a team whose Pro trial has lapsed without anyone resolving it.
 *
 * Deliberately not dismissible — no close button, and Escape and backdrop clicks do nothing. The
 * team is still sitting on Pro it is no longer entitled to, and the whole point of the flow is to
 * get a decision rather than let it be dismissed on every login indefinitely.
 */
export const ProTrialEndedModal: React.FC<ProTrialEndedModalProps> = ({
    isOpen,
    onUpgradeClick,
    onDowngradeClick,
}) => {
    const theme = useTheme();

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => { /* not dismissible -- the user must choose */ }}
            title="Your Pro Trial Has Ended"
            showCloseButton={false}
            // Mandatory, non-dismissible -- must always paint above an ordinary case-level modal
            // that happens to be open, not just whichever one mounted later in the DOM.
            zIndex={ELEVATED_MODAL_Z_INDEX}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
                <div style={{ display: 'flex', gap: theme.spacing.md, alignItems: 'flex-start' }}>
                    <FontAwesomeIcon
                        icon={faGem}
                        style={{ color: theme.colors.primary, fontSize: theme.typography.fontSize.xxl, marginTop: '2px' }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                        <p style={{ margin: 0, color: theme.colors.text.primary }}>
                            Your team's Pro trial has ended, so your team will be moved to Standard.
                        </p>
                        <p style={{ margin: 0, color: theme.colors.text.secondary }}>
                            Upgrade to keep Pro features — split custody, division exclusions, the
                            high income estimator, and the rest — or move to Standard now.
                        </p>
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing.md }}>
                    <button
                        type="button"
                        onClick={onUpgradeClick}
                        style={{
                            width: '100%',
                            padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                            borderRadius: theme.borderRadius.sm,
                            border: `1px solid ${theme.colors.primary}`,
                            backgroundColor: theme.colors.primary,
                            color: theme.colors.text.inverse,
                            fontSize: theme.typography.fontSize.lg,
                            fontWeight: theme.typography.fontWeight.semibold,
                            cursor: 'pointer',
                        }}
                    >
                        <FontAwesomeIcon icon={faGem} style={{ marginRight: theme.spacing.sm }} />
                        Upgrade to Pro
                    </button>

                    {/* Deliberately understated next to the primary action. */}
                    <button
                        type="button"
                        onClick={onDowngradeClick}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: theme.colors.text.secondary,
                            fontSize: theme.typography.fontSize.sm,
                            textDecoration: 'underline',
                            cursor: 'pointer',
                        }}
                    >
                        Downgrade to Standard
                    </button>
                </div>
            </div>
        </Modal>
    );
};
